import { useState, useMemo } from 'react'
import { Copy, ExternalLink, CheckCircle } from '@/ui/icons'
import { Card, CardContent, Badge } from '@/ui/design-system'
import { getAllWaypointsFromTrip, resolveMeetupDisplay } from '@/modules/trips/utils/meetupWaypointSync'
import { MemberResponse } from '@/shared/api'
import { useTripRouteSchedule } from '@/modules/routes/hooks/useTripRouteSchedule'
import { useMealNeeds } from '@/modules/meals/hooks/useMealNeeds'
import { formatDateForDisplay } from '@/modules/trips/utils/dateHelpers'
import { getUserProfileUrl } from '@/shared/utils/userImages'
import type { WaypointResponse } from '@/shared/api/routes'
import type { MealResponseDto } from '@/shared/types/meals'

interface GuestTripContentProps {
  tripId: string
  trip: {
    radio_channel?: string | null
    radio_frequency?: string | null
    radio_type?: string | null
    meetup_location?: string | null
    meetup_time?: string | null
    waypoints?: WaypointResponse[]
    gear?: Array<{
      id: string
      name: string
      category?: string | null
      quantity?: number | null
      notes?: string | null
    }>
    // Accept either MealResponseDto[] (from authenticated trips) or guest format (with meal_name)
    meals?: MealResponseDto[] | Array<{
      id: string
      meal_name?: string
      name?: string
      planned_date: string
      planned_meal_time?: string
      meal_type?: string
    }>
    routeSchedule?: {
      tripId: string
      startDate: string | null
      endDate: string | null
      days: any[]
      unscheduled: Array<{
        association: any
        route: any
        waypoints: Array<{
          id: string
          route_id: string
          name: string
          latitude: number
          longitude: number
          order_index: number
        }>
      }>
    }
  }
  members: MemberResponse[]
}

function getMemberDisplayName(member: MemberResponse): string {
  const metadata = member.user?.user_metadata ?? {}
  const metadataName =
    (typeof metadata.full_name === 'string' && metadata.full_name) ||
    (typeof metadata.name === 'string' && metadata.name) ||
    (typeof metadata.display_name === 'string' && metadata.display_name) ||
    null

  if (metadataName) {
    return metadataName
  }

  if (member.guest_name) {
    return member.guest_name
  }

  if (member.user?.email) {
    return member.user.email
  }

  return 'Unknown'
}

function getMemberInitials(member: MemberResponse): string {
  if (member.guest_name) {
    return member.guest_name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  if (member.user?.user_metadata?.full_name) {
    return member.user.user_metadata.full_name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  if (member.user?.email) {
    return member.user.email[0].toUpperCase()
  }

  return '?'
}

function getAvatarColor(member: MemberResponse): string {
  const colors = [
    'bg-adventure-100 text-adventure-700',
    'bg-earth-100 text-earth-700',
    'bg-adventure-200 text-adventure-800',
    'bg-earth-200 text-earth-800',
    'bg-adventure-300 text-adventure-900',
    'bg-earth-300 text-earth-900'
  ]
  
  const hash = member.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

export function GuestTripContent({ tripId, trip, members }: GuestTripContentProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'crew'>('details')
  const [copiedCoords, setCopiedCoords] = useState<string | null>(null)
  
  // Use route schedule from trip data if available (for guests), otherwise try to fetch
  const hasAuthToken = typeof window !== 'undefined' && localStorage.getItem('access_token')
  const shouldFetchRouteSchedule = !trip.routeSchedule && hasAuthToken
  const { schedule: fetchedRouteSchedule, loading: routeLoading } = useTripRouteSchedule(
    tripId, 
    { skip: !shouldFetchRouteSchedule }
  )
  
  // Use route schedule from trip data (for guests) or fetched data (for authenticated users)
  const routeSchedule = trip.routeSchedule || fetchedRouteSchedule
  
  // Use meals from trip data if available (for guests), otherwise try to fetch
  const shouldFetchMeals = !trip.meals && hasAuthToken
  const { meals: fetchedMeals, isLoading: mealsLoading, error: mealsError } = useMealNeeds(tripId, { skip: !shouldFetchMeals })
  
  // Use meals from trip data (for guests) or fetched data (for authenticated users)
  const meals = trip.meals || fetchedMeals

  // Helper to check if a string is coordinates
  const isCoordinates = (str: string | null | undefined): boolean => {
    if (!str) return false
    const coordPattern = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/
    return coordPattern.test(str.trim())
  }

  // Helper to parse coordinates
  const parseCoordinates = (str: string): { lat: number; lon: number } | null => {
    if (!isCoordinates(str)) return null
    const parts = str.split(',').map(p => p.trim())
    const lat = parseFloat(parts[0])
    const lon = parseFloat(parts[1])
    if (isNaN(lat) || isNaN(lon)) return null
    return { lat, lon }
  }

  // Helper to copy coordinates to clipboard
  const copyCoordinates = async (coords: string) => {
    try {
      await navigator.clipboard.writeText(coords)
      setCopiedCoords(coords)
      setTimeout(() => setCopiedCoords(null), 2000)
    } catch (error) {
    }
  }

  // Helper to get Google Maps URL
  const getGoogleMapsUrl = (coords: string | { lat: number; lon: number }): string => {
    if (typeof coords === 'string') {
      const parsed = parseCoordinates(coords)
      if (!parsed) return '#'
      return `https://www.google.com/maps?q=${parsed.lat},${parsed.lon}`
    }
    return `https://www.google.com/maps?q=${coords.lat},${coords.lon}`
  }

  const allWaypointsForMeetup = useMemo(() => getAllWaypointsFromTrip(trip), [trip])

  const meetupDisplay = useMemo(
    () => resolveMeetupDisplay(trip, allWaypointsForMeetup),
    [trip, allWaypointsForMeetup]
  )

  // Build itinerary data from route schedule
  const itineraryDays = useMemo(() => {
    if (!routeSchedule || !routeSchedule.days || routeSchedule.days.length === 0) {
      return []
    }

    return routeSchedule.days.map((day: any) => {
      const allWaypoints: any[] = []
      
      if (day.routes && Array.isArray(day.routes)) {
        day.routes.forEach((routeEntry: any) => {
          if (routeEntry.waypoints && Array.isArray(routeEntry.waypoints)) {
            const sortedWaypoints = [...routeEntry.waypoints].sort((a: any, b: any) => {
              const aOrder = a.order_index ?? 0
              const bOrder = b.order_index ?? 0
              return aOrder - bOrder
            })
            allWaypoints.push(...sortedWaypoints)
          }
        })
      }

      const uniqueWaypoints = Array.from(
        new Map(allWaypoints.map((wp: any) => [wp.id, wp])).values()
      )

      return {
        day: day.dayIndex !== undefined ? day.dayIndex + 1 : null,
        date: day.date || null,
        label: day.label || (day.dayIndex !== undefined ? `Day ${day.dayIndex + 1}` : 'Day'),
        waypoints: uniqueWaypoints
      }
    })
  }, [routeSchedule])

  // Create meals summary with structured data
  const mealsByDate = useMemo(() => {
    const mealsToUse = Array.isArray(meals) ? meals : []
    
    if ((mealsLoading && shouldFetchMeals) || !mealsToUse || mealsToUse.length === 0) {
      return null
    }

    const grouped: Record<string, Record<string, string[]>> = {}

    mealsToUse.forEach((meal: any) => {
      const mealTime = meal.planned_meal_time || meal.meal_type || 'snack'
      const mealName = meal.meal_name || meal.name || 'Unnamed meal'
      const mealDate = meal.planned_date || 'Unscheduled'
      
      if (!grouped[mealDate]) {
        grouped[mealDate] = {
          breakfast: [],
          lunch: [],
          dinner: [],
          snack: []
        }
      }
      
      if (grouped[mealDate][mealTime]) {
        grouped[mealDate][mealTime].push(mealName)
      }
    })

    const sortedDates = Object.keys(grouped).sort((a, b) => {
      if (a === 'Unscheduled') return 1
      if (b === 'Unscheduled') return -1
      return a.localeCompare(b)
    })

    return sortedDates.map((date) => ({
      date,
      dateLabel: date === 'Unscheduled' ? 'Unscheduled' : formatDateForDisplay(date),
      meals: grouped[date]
    }))
  }, [meals, mealsLoading, shouldFetchMeals])

  // Meal time configuration (bgColor used for Badge styling)
  const mealTimeConfig = {
    breakfast: { label: 'Breakfast', color: 'text-adventure-600', bgColor: 'bg-adventure-50' },
    lunch: { label: 'Lunch', color: 'text-adventure-500', bgColor: 'bg-adventure-50' },
    dinner: { label: 'Dinner', color: 'text-adventure-700', bgColor: 'bg-adventure-50' },
    snack: { label: 'Snacks', color: 'text-earth-600', bgColor: 'bg-earth-50' }
  }

  return (
    <div className="w-full">
      {/* Tab Navigation */}
      <div className="border-b border-earth-200 mb-6">
        <div className="flex space-x-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'details'
                ? 'border-adventure-600 text-adventure-700 bg-adventure-50/50'
                : 'border-transparent text-earth-600 hover:text-earth-900 hover:border-earth-300'
            }`}
          >
            Trip Details
          </button>
          <button
            onClick={() => setActiveTab('crew')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'crew'
                ? 'border-adventure-600 text-adventure-700 bg-adventure-50/50'
                : 'border-transparent text-earth-600 hover:text-earth-900 hover:border-earth-300'
            }`}
          >
            Crew
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTab === 'details' ? (
          <div className="space-y-6">
            {/* Itinerary - Full Width */}
            <Card>
              <CardContent className="p-6">
                <div>
                  <h3 className="text-base font-semibold text-earth-900 mb-2">Itinerary</h3>
                    <p className="text-sm text-earth-600 mb-4">Daily schedule and activities</p>
                    {routeLoading && shouldFetchRouteSchedule ? (
                      <div className="text-sm text-earth-500 italic">Loading...</div>
                    ) : itineraryDays.length > 0 ? (
                      <div className="space-y-4">
                        {itineraryDays.map((day, dayIndex) => (
                          <div key={dayIndex} className="border-b border-earth-200 last:border-b-0 pb-4 last:pb-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-earth-900">
                                {day.label}
                              </span>
                              {day.date && (
                                <span className="text-xs text-earth-500">
                                  {formatDateForDisplay(day.date)}
                                </span>
                              )}
                            </div>
                            {day.waypoints && day.waypoints.length > 0 ? (
                              <div className="ml-0 space-y-2">
                                {day.waypoints.map((waypoint: any, wpIndex: number) => {
                                  const hasCoords = waypoint.latitude != null && waypoint.longitude != null
                                  const coords = hasCoords ? `${waypoint.latitude}, ${waypoint.longitude}` : null
                                  const isCopied = coords && copiedCoords === coords
                                  const isMeetupSpot = waypoint.poi_type === 'meetup_spot'

                                  return (
                                    <div key={waypoint.id || wpIndex} className="text-sm text-earth-700">
                                      <div className="flex flex-wrap items-center gap-2 font-medium">
                                        <span>{waypoint.name || (hasCoords ? coords : 'Unnamed waypoint')}</span>
                                        {isMeetupSpot && (
                                          <Badge variant="secondary" className="text-[10px] font-medium bg-adventure-100 text-adventure-800 border-0">
                                            Meetup
                                          </Badge>
                                        )}
                                      </div>
                                      {hasCoords && coords && waypoint.name && (
                                        <div className="flex items-center gap-2 mt-0.5">
                                          <button
                                            onClick={() => copyCoordinates(coords)}
                                            className="text-xs font-mono text-earth-600 hover:text-primary cursor-pointer flex items-center gap-1 transition-colors"
                                            title="Click to copy coordinates"
                                          >
                                            {coords}
                                            {isCopied ? (
                                              <CheckCircle className="w-3 h-3 text-green-600 ml-1" />
                                            ) : (
                                              <Copy className="w-3 h-3 text-earth-400 ml-1" />
                                            )}
                                          </button>
                                          <a
                                            href={getGoogleMapsUrl({ lat: waypoint.latitude, lon: waypoint.longitude })}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                                          >
                                            <ExternalLink className="w-3 h-3" />
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <div className="text-xs text-earth-500 ml-0 italic">No waypoints</div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-earth-500 italic">No itinerary has been planned yet.</div>
                    )}
                </div>
              </CardContent>
            </Card>

            {/* Radio Channel, Meetup Location, Meals - Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Radio Channel */}
              <Card>
                <CardContent className="p-6">
                  <div>
                    <h3 className="text-base font-semibold text-earth-900 mb-2">Radio Channel</h3>
                      {trip.radio_channel || trip.radio_frequency ? (
                        <div className="space-y-1">
                          {trip.radio_channel && (
                            <div className="text-base font-semibold text-earth-900">{trip.radio_channel}</div>
                          )}
                          {trip.radio_frequency && (
                            <div className="text-sm text-earth-600">{trip.radio_frequency}</div>
                          )}
                          {trip.radio_type && (
                            <div className="text-xs text-earth-500 capitalize">{trip.radio_type}</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-earth-500">Not set</div>
                      )}
                  </div>
                </CardContent>
              </Card>

              {/* Meetup Location (from itinerary meetup_spot waypoint or legacy trip fields) */}
              <Card>
                <CardContent className="p-6">
                  <div>
                    <h3 className="text-base font-semibold text-earth-900 mb-2">Meetup Location</h3>
                      {meetupDisplay.locationLabel ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            {meetupDisplay.locationIsCoordinates ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() =>
                                    copyCoordinates(meetupDisplay.coordsForMaps || meetupDisplay.locationLabel!)
                                  }
                                  className="text-base font-semibold text-earth-900 hover:text-primary cursor-pointer flex items-center gap-1.5 transition-colors"
                                  title="Click to copy coordinates"
                                >
                                  {meetupDisplay.locationLabel}
                                  {copiedCoords === (meetupDisplay.coordsForMaps || meetupDisplay.locationLabel) ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-earth-400" />
                                  )}
                                </button>
                                <a
                                  href={getGoogleMapsUrl(
                                    meetupDisplay.coordsForMaps || meetupDisplay.locationLabel
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Open in Maps
                                </a>
                              </>
                            ) : (
                              <div className="text-base font-semibold text-earth-900">{meetupDisplay.locationLabel}</div>
                            )}
                          </div>
                          {meetupDisplay.coordsForMaps &&
                            meetupDisplay.locationLabel &&
                            !meetupDisplay.locationIsCoordinates && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => copyCoordinates(meetupDisplay.coordsForMaps!)}
                                  className="text-sm font-medium text-earth-700 hover:text-primary cursor-pointer flex items-center gap-1.5 transition-colors"
                                  title="Copy coordinates"
                                >
                                  {meetupDisplay.coordsForMaps}
                                  {copiedCoords === meetupDisplay.coordsForMaps ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-earth-400" />
                                  )}
                                </button>
                                <a
                                  href={getGoogleMapsUrl(meetupDisplay.coordsForMaps)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Open pin in Maps
                                </a>
                              </div>
                            )}
                          {meetupDisplay.timeFormatted && (
                            <div className="text-sm text-earth-600">{meetupDisplay.timeFormatted}</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-earth-500">Not set</div>
                      )}
                  </div>
                </CardContent>
              </Card>

              {/* Meals */}
              <Card className="md:col-span-2">
                <CardContent className="p-6">
                  <div>
                    <h3 className="text-base font-semibold text-earth-900 mb-2">Meals</h3>
                      {mealsLoading && shouldFetchMeals ? (
                        <div className="text-sm text-earth-500">Loading...</div>
                      ) : mealsError && shouldFetchMeals ? (
                        <div className="text-sm text-earth-500">No meals planned</div>
                      ) : mealsByDate && mealsByDate.length > 0 ? (
                        <div className="space-y-3">
                          {mealsByDate.slice(0, 2).map((dateGroup) => (
                            <div key={dateGroup.date} className="border-b border-earth-200 last:border-b-0 pb-2 last:pb-0">
                              <h4 className="text-xs font-semibold text-earth-700 mb-2">{dateGroup.dateLabel}</h4>
                              <div className="flex flex-wrap gap-1.5">
                                {Object.entries(dateGroup.meals).flatMap(([mealTime, mealNames]) => 
                                  mealNames.map((mealName, idx) => {
                                    const config = mealTimeConfig[mealTime as keyof typeof mealTimeConfig] || mealTimeConfig.snack
                                    return (
                                      <Badge
                                        key={`${dateGroup.date}-${mealTime}-${idx}`}
                                        variant="outline"
                                        className={`${config.bgColor} border-0 text-xs font-normal`}
                                      >
                                        {mealName}
                                      </Badge>
                                    )
                                  })
                                )}
                              </div>
                            </div>
                          ))}
                          {mealsByDate.length > 2 && (
                            <div className="text-xs text-earth-500">+{mealsByDate.length - 2} more days</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-earth-500">No meals have been planned.</div>
                      )}
                  </div>
                </CardContent>
              </Card>

              {/* Gear */}
              {trip.gear && trip.gear.length > 0 && (
                <Card className="md:col-span-2">
                  <CardContent className="p-6">
                    <div>
                      <h3 className="text-base font-semibold text-earth-900 mb-2">Gear</h3>
                      <div className="space-y-2">
                        {trip.gear.map((item) => (
                            <div key={item.id} className="border-b border-earth-200 last:border-b-0 pb-2 last:pb-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-earth-900">{item.name}</div>
                                  {item.category && (
                                    <div className="text-xs text-earth-500 capitalize mt-0.5">{item.category}</div>
                                  )}
                                  {item.notes && (
                                    <div className="text-xs text-earth-600 mt-1">{item.notes}</div>
                                  )}
                                </div>
                                {item.quantity && item.quantity > 1 && (
                                  <Badge variant="outline" className="text-xs bg-earth-50 border-earth-200 text-earth-700">
                                    Qty: {item.quantity}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>
          </div>
        ) : (
          /* Crew Tab */
          <div>
            <Card>
              <CardContent className="p-6">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-earth-900 mb-1">Trip Crew</h3>
                  <p className="text-sm text-earth-600">{members.length} {members.length === 1 ? 'member' : 'members'} on this adventure</p>
                </div>
                {members && members.length > 0 ? (
                  <div className="space-y-3">
                    {(() => {
                      const primary = members.filter((m) => !m.parent_member_id)
                      const tagAlongsByParent = members
                        .filter((m) => m.parent_member_id != null)
                        .reduce<Record<string, MemberResponse[]>>((acc, m) => {
                          const pid = m.parent_member_id!
                          if (!acc[pid]) acc[pid] = []
                          acc[pid].push(m)
                          return acc
                        }, {})
                      return primary.map((member) => {
                        const initials = getMemberInitials(member)
                        const displayName = getMemberDisplayName(member)
                        const avatarColor = getAvatarColor(member)
                        const avatarUrl =
                          member.member_type === 'full_member' && member.user_profile?.profile_image_path
                            ? getUserProfileUrl(member.user_profile.profile_image_path)
                            : null
                        const tagAlongs = tagAlongsByParent[member.id] ?? []
                        return (
                          <div key={member.id} className="space-y-1.5">
                            <div className="flex items-center space-x-3">
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt=""
                                  className="flex-shrink-0 w-10 h-10 rounded-full object-cover border border-earth-200 dark:border-earth-600"
                                />
                              ) : (
                                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${avatarColor}`}>
                                  {initials}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-earth-900">{displayName}</span>
                                  {member.role && member.role !== 'member' && (
                                    <Badge 
                                      variant="secondary" 
                                      className="text-xs px-1.5 py-0 bg-earth-100 text-earth-700 border-0 capitalize"
                                    >
                                      {member.role}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            {tagAlongs.map((ta) => {
                              const taInitials = getMemberInitials(ta)
                              const taName = getMemberDisplayName(ta)
                              const taColor = getAvatarColor(ta)
                              const taAvatarUrl =
                                ta.member_type === 'full_member' && ta.user_profile?.profile_image_path
                                  ? getUserProfileUrl(ta.user_profile.profile_image_path)
                                  : null
                              return (
                                <div key={ta.id} className="flex items-center space-x-3 pl-6">
                                  {taAvatarUrl ? (
                                    <img
                                      src={taAvatarUrl}
                                      alt=""
                                      className="flex-shrink-0 w-8 h-8 rounded-full object-cover border border-earth-200 dark:border-earth-600"
                                    />
                                  ) : (
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-medium text-xs ${taColor}`}>
                                      {taInitials}
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm text-earth-700 dark:text-earth-300">{taName}</span>
                                    <span className="text-xs text-earth-500 ml-2">Tag-along</span>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })
                    })()}
                  </div>
                ) : (
                  <div className="text-sm text-earth-500">No members</div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
